import { Button, Form, InputNumber, Select, Space, Typography } from "antd";
import { useState } from "react";
import _t from "../../lang/translate";
import { OpenModalProps } from "../../types/modalTypes";
import assertFC from "../../utilities/assertFC";
import { renderErrorMessage } from "../messages/errorMessage";
import { useApi } from "../../services/useApi";
import { subscriptionPackageUrl } from "../../services/subcriptionService";
import { SubscriptionPackageType } from "../../types/subscriptionTypes";
import { giveTrial } from "../../services/dealerService";
import { IdType } from "../../types/appTypes";

const { Text } = Typography;

type Payload = {
  dealerId: IdType;
};

const selectablePackages = (packages: SubscriptionPackageType[]) =>
  packages.filter(p => p.selectable && p.priceNoVat > 0);

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
};

export default function GiveTrial<T extends object>({ onSuccess, onCancel, payload }: OpenModalProps<T>) {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<{ subscriptionPackageId: IdType; trialDays: number }>();
  const { dealerId } = payload as Payload;

  const [{ data: packagesData }] = useApi<{ data: SubscriptionPackageType[] }>(subscriptionPackageUrl, { data: [] });
  const packages = selectablePackages(packagesData.data);

  const handleSubmit = async (values: { subscriptionPackageId: IdType; trialDays: number }) => {
    try {
      setLoading(true);
      const { data } = await giveTrial(dealerId, values);
      onSuccess(data as T);
    } catch (error) {
      renderErrorMessage(error);
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      initialValues={{ trialDays: 30, subscriptionPackageId: null }}
      onFinish={handleSubmit}
      {...formItemLayout}
    >
      <h2 className="modal-title">{_t("giveTrialTitle")}</h2>

      <Form.Item
        label={_t("selectPackage")}
        name="subscriptionPackageId"
        rules={[{ required: true }]}
      >
        <Select placeholder={_t("choose")}>
          {packages.map(pkg => (
            <Select.Option key={pkg.id} value={pkg.id}>{pkg.title}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={_t("trialDays")}
        name="trialDays"
        help={_t("trialDaysHelp")}
        rules={[
          { required: true },
          { type: 'number', min: 1, max: 180 }
        ]}
      >
        <InputNumber min={1} max={180} />
      </Form.Item>

      <div className="text-right">
        <Space>
          <Button type="ghost" loading={loading} onClick={onCancel}>
            {_t("cancel")}
          </Button>
          <Button htmlType="submit" loading={loading} type="primary">
            {_t("giveTrialSubmit")}
          </Button>
        </Space>
      </div>
    </Form>
  );
}

assertFC(GiveTrial);
